<template>
  <div class="appointment-booked-screen">
    <lottie-player
      src="/assets/lottie-confetti.json"
      class="lottie-confetti"
      background="transparent"
      speed="1"
      autoplay
    />
    <div class="appointment-details">
      <div class="appointment-time-wrapper">
        <p class="text-header-4">
          We messaged you all the details and are excited to see you on
          <strong>{{ appointmentTimeFormatted }}</strong>
        </p>
      </div>
      <div class="appointment-icon-service-wrapper">
        <div class="appointment-icon">
          <!-- SVG wrench omitted for brevity -->
        </div>
        <p class="text-header-4 appointment-service-detail">
          {{ selectedServiceDescription }}
        </p>
      </div>
      <hr class="divider" />
      <div v-if="appointmentType === 'drop_off'" class="appointment-type-detail">
        <div class="text-header-4">
          <strong>Drop-off appointment</strong>
        </div>
        <p class="text-header-4" v-if="dropOffPolicy">
          {{ dropOffPolicy }}
        </p>
        <p class="text-header-4" v-else>
          Head to the shop and leave your vehicle with us. If you have questions, just ask our team.
        </p>
      </div>
      <div v-else class="appointment-type-detail">
        <div class="text-header-4">
          <strong>Waiting appointment</strong>
        </div>
        <p class="text-header-4">Head to the shop and relax in our waiting room while we take care of your vehicle.</p>
      </div>
    </div>

    <fixed-shop-map :selectedAccountLocation="selectedAccountLocation" />

    <div class="box-actions sticked-to-bottom-button-wrapper">
      <button @click="openGeoLink" class="secondary-btn outline half-width">Get directions</button>
      <button id="calendar-button" class="primary-btn half-width">Add to calendar</button>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import ShopServicesService from '@/services/ShopServicesService';
import { atcb_init, atcb_action } from 'add-to-calendar-button';
import 'add-to-calendar-button/assets/css/atcb.css';
import FixedShopMap from './FixedShopMap';

export default {
  name: 'AppointmentBookedScreen',
  components: { FixedShopMap },
  props: {
    accountSlug: { type: String, required: true },
    selectedAccountLocation: { type: Object, required: false },
    dropOffPolicy: { type: String, default: '' },
    appointmentTime: { type: String, default: '' },
    appointmentType: { type: String, default: 'waiting' },
    selectedServices: { type: Array, required: false },
    selectedServiceAdditionalNotes: { type: String, required: false },
    diagnosticPrice: { type: Object, required: true },
    requestedServiceDescription: { type: String, required: false },
    deferredJobs: { type: Array, default: () => [] },
  },
  data() {
    return {
      shopInfo: {},
      eventData: {},
    };
  },
  computed: {
    appointmentTimeFormatted() {
      return moment(this.appointmentTime).format('MMMM Do [at] h:mm a');
    },
    selectedServiceDescription() {
      let base = this.selectedServices?.length
        ? this.selectedServices.map((s) => s.service_name).join(', ')
        : this.requestedServiceDescription || '';
      if (this.selectedServiceAdditionalNotes) {
        base += ' – ' + this.selectedServiceAdditionalNotes;
      }
      if (this.deferredJobs.length) {
        base += ', ' + this.deferredJobs.map((j) => j.name).join(', ');
      }
      return base;
    },
  },
  methods: {
    async openGeoLink() {
      const ua = window.navigator.userAgent.toLowerCase();
      const isIos = /iphone|ipad|ipod/.test(ua);
      const placeId = this.shopInfo.gmb_place_id;
      const lat = this.shopInfo.lat;
      const lng = this.shopInfo.lng;
      const name = this.shopInfo.gmb_place_name || this.shopInfo.name;

      try {
        if (isIos) {
          // --- iOS: deep‑link into Google Maps if Place ID, else Apple Maps coords
          if (placeId) {
            // Google Maps app
            window.top.location.href = ['comgooglemaps://?', `q=${name}`, `query_place_id=${placeId}`].join('&');
          } else {
            // fallback to Apple's Maps app with lat/lng
            window.top.location.href = `maps://maps.google.com/maps?daddr=${lat},${lng}&ll=`;
          }
        } else {
          // --- Desktop/Mobile Web: open GMB panel via /search/
          const url = placeId
            ? `https://www.google.com/maps/search/?api=1&query=${name}&query_place_id=${placeId}`
            : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

          window.open(url, '_blank');
        }
      } catch (err) {
        alert(err.message);
      }
    },

    sendAnalyticsData() {
      const reportingData = {
        event: 'appointmentBooked',
        accountSlug: this.accountSlug,
        appointmentService: this.selectedServices?.length
          ? this.selectedServices.map((s) => s.service_name).join(', ')
          : 'Diagnostic',
      };
      window.parent.postMessage({ name: 'reporting.dataLayerEvent', payload: reportingData }, '*');
    },
  },
  async mounted() {
    const { data } = await ShopServicesService.get.shopInfo(this.accountSlug);
    this.shopInfo = data.data;

    // Calendar button setup omitted for brevity…
    this.sendAnalyticsData();
    this.$nextTick(atcb_init);
    document.querySelector('#calendar-button').addEventListener('click', () => atcb_action(this.eventData));
  },
};
</script>

<style scoped>
.lottie-confetti {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* …other styles unchanged… */
</style>
